import { type CartItem } from "@/contexts/CartContext";

type Props = {
  items: CartItem[];
  subtotal: number;
  shipping: number;
  total: number;
};

export default function OrderSummary({ items, subtotal, shipping, total }: Props) {
  return (
    <aside className="lg:sticky lg:top-28">
      <div className="rounded-lg bg-background-100/70 border border-background-200/70 overflow-hidden">
        <ul className="divide-y divide-background-200/70 max-h-[320px] overflow-y-auto">
          {items.map((it) => (
            <li key={it.lineId} className="px-5 py-4 flex gap-3">
              <div className="relative shrink-0 w-14 h-16 rounded-md overflow-hidden bg-background-100">
                {it.image && (
                  <img
                    src={it.image}
                    alt={it.productName}
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground-950 leading-snug line-clamp-2">
                  {it.productName}
                </p>
                {it.skuLabel && (
                  <p className="text-xs text-foreground-600 mt-0.5">{it.skuLabel}</p>
                )}
                <p className="text-xs text-foreground-700 mt-1">Qty {it.quantity}</p>
              </div>
              <p className="text-sm font-medium text-foreground-950 self-start">
                ${(it.finalPrice * it.quantity).toFixed(2)}
              </p>
            </li>
          ))}
        </ul>

        <div className="px-5 py-4 border-t border-background-200/70 space-y-2">
          <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
          <Row
            label="Shipping"
            value={shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}
          />
        </div>

        <div className="px-5 py-4 border-t border-background-200/70 bg-background-50 flex items-baseline justify-between">
          <span className="text-sm font-medium text-foreground-950">Total</span>
          <span className="text-lg font-medium text-foreground-950">
            ${total.toFixed(2)}
          </span>
        </div>
      </div>
    </aside>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-foreground-700">{label}</span>
      <span className="text-foreground-950 font-medium">{value}</span>
    </div>
  );
}